import { ref, watch } from 'vue'

// Backups are set per device: never synced, never part of an export.
const LOCAL_BACKUP_STORAGE_KEY = 'ink-local-backup-settings'

export interface BackupTier {
  id: string
  label: string
  bucketUnit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months'
  bucketWidth: number
  maxBuckets: number
}

export interface BackupSettings {
  automaticEnabled: boolean
  periodicEnabled: boolean
  periodicMinutes: number
  idleEnabled: boolean
  idleMinutes: number
  onCloseEnabled: boolean
  onLaunchEnabled: boolean
  format: 'zip' | 'tar.gz' | 'folder'
  includeMarkdown: boolean
  destination: string | null
  tiers: BackupTier[]
  maxStorageMb: number | null
}

export const defaultBackupSettings: BackupSettings = {
  automaticEnabled: true,
  periodicEnabled: true,
  periodicMinutes: 30,
  idleEnabled: false,
  idleMinutes: 10,
  onCloseEnabled: true,
  onLaunchEnabled: true,
  format: 'zip',
  includeMarkdown: true,
  destination: null,
  tiers: [
    { id: 'recent', label: 'Recent', bucketUnit: 'hours', bucketWidth: 1, maxBuckets: 24 },
    { id: 'daily', label: 'Daily', bucketUnit: 'days', bucketWidth: 1, maxBuckets: 14 },
    { id: 'monthly', label: 'Monthly', bucketUnit: 'months', bucketWidth: 1, maxBuckets: 12 },
  ],
  maxStorageMb: null,
}

function loadBackupSettings(): BackupSettings {
  try {
    const saved = JSON.parse(localStorage.getItem(LOCAL_BACKUP_STORAGE_KEY) || '{}')
    return {
      ...defaultBackupSettings,
      ...saved,
      tiers: Array.isArray(saved.tiers) ? saved.tiers : structuredClone(defaultBackupSettings.tiers),
    }
  } catch {
    return structuredClone(defaultBackupSettings)
  }
}

/** This device's backup settings, saved as they change. */
export function useBackupSettings() {
  const backupSettings = ref<BackupSettings>(loadBackupSettings())
  watch(backupSettings, (value) => {
    localStorage.setItem(LOCAL_BACKUP_STORAGE_KEY, JSON.stringify(value))
  }, { deep: true })
  return backupSettings
}
